import { motion, AnimatePresence } from "framer-motion";
import { Tag, X } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/Reveal";
import { BookingSuccess } from "@/components/booking/BookingSuccess";
import { ClientTypeStep, NeedsStep } from "@/components/booking/ProfileSteps";
import { SlotStep } from "@/components/booking/SlotStep";
import { ContactStep } from "@/components/booking/ContactStep";
import { BookingNav } from "@/components/booking/BookingNav";
import { useBookingForm, useCalendarStatus, useOffer, useSlots } from "@/hooks/useBooking";

const steps = ["Vous êtes", "Vos besoins", "Votre créneau", "Vos coordonnées"];
const STEP_ANIM = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: -16 }, transition: { duration: 0.35 } };

const Progress = ({ step }) => (
  <>
    <div className="mx-auto mt-12 flex max-w-xl items-center justify-between" data-testid="booking-progress">
      {steps.map((s, i) => (
        <div key={s} className="flex items-center">
          <div className={`flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold transition-colors duration-300 ${i <= step ? "bg-argia-red text-white" : "bg-argia-stone text-argia-muted"}`}>
            {i + 1}
          </div>
          {i < steps.length - 1 && <div className={`mx-2 h-px w-8 sm:w-14 ${i < step ? "bg-argia-red" : "bg-argia-stone"}`} />}
        </div>
      ))}
    </div>
    <p className="mt-3 text-center text-xs font-semibold uppercase tracking-[0.2em] text-argia-muted">{steps[step]}</p>
  </>
);

const OfferBanner = ({ offer, onClear }) => (
  <div className="mx-auto mt-8 flex max-w-2xl items-center justify-between gap-4 rounded-2xl border border-argia-red/30 bg-white px-5 py-4" data-testid="booking-offer-banner">
    <div className="flex items-center gap-3">
      <Tag size={18} strokeWidth={1.5} className="shrink-0 text-argia-red" />
      <p className="text-sm text-argia-ink">
        <span className="text-argia-muted">Offre présélectionnée : </span>
        <span className="font-semibold" data-testid="booking-offer-name">{offer}</span>
      </p>
    </div>
    <button onClick={onClear} data-testid="booking-offer-clear" aria-label="Retirer l'offre" className="shrink-0 rounded-full p-1.5 text-argia-muted transition-colors hover:bg-argia-stone hover:text-argia-ink">
      <X size={16} strokeWidth={1.5} />
    </button>
  </div>
);

const StepPanel = ({ b, slots, slotsLoading }) => {
  switch (b.step) {
    case 0: return <ClientTypeStep clientType={b.clientType} onSelect={b.selectClientType} />;
    case 1: return <NeedsStep clientType={b.clientType} needs={b.needs} onToggle={b.toggleNeed} />;
    case 2: return <SlotStep date={b.date} onDateChange={b.selectDate} slots={slots} slotsLoading={slotsLoading} selectedSlot={b.selectedSlot} onSelectSlot={b.setSelectedSlot} />;
    default: return <ContactStep form={b.form} onChange={b.setForm} clientType={b.clientType} />;
  }
};

export default function Booking() {
  const { offer, clear: clearOffer } = useOffer();
  const calendarConnected = useCalendarStatus();
  const b = useBookingForm(offer);
  const { slots, loading: slotsLoading } = useSlots(b.date);

  if (b.confirmed) return <BookingSuccess confirmed={b.confirmed} />;

  return (
    <div className="mx-auto max-w-4xl px-6 pb-24 pt-16 lg:pt-24" data-testid="booking-page">
      <Reveal className="text-center">
        <div className="flex justify-center"><SectionLabel>Prise de rendez-vous</SectionLabel></div>
        <h1 className="mx-auto mt-6 max-w-2xl font-display text-4xl font-bold leading-[1.08] tracking-tight text-argia-ink sm:text-5xl" data-testid="booking-title">
          Quelques questions, et c'est réservé
        </h1>
        <p className="mx-auto mt-5 max-w-xl text-base text-argia-muted md:text-lg">
          Un questionnaire rapide pour préparer notre échange. Le premier rendez-vous découverte est offert.
        </p>
      </Reveal>

      {offer && <OfferBanner offer={offer} onClear={clearOffer} />}
      <Progress step={b.step} />

      <AnimatePresence mode="wait">
        <motion.div key={b.step} {...STEP_ANIM} className="mx-auto mt-10 max-w-2xl">
          <StepPanel b={b} slots={slots} slotsLoading={slotsLoading} />
        </motion.div>
      </AnimatePresence>

      <BookingNav step={b.step} canNext={b.canNext} submitting={b.submitting} onBack={b.back} onNext={b.next} onSubmit={b.submit} />

      {calendarConnected === false && (
        <p className="mx-auto mt-10 max-w-2xl text-center text-xs text-argia-muted" data-testid="calendar-disconnected-note">
          Les créneaux affichés sont indicatifs — Aurélie confirmera votre rendez-vous personnellement.
        </p>
      )}
    </div>
  );
}
